<script>
  import {getContext, onDestroy, onMount} from 'svelte';
  import {SERVICES} from '@/const/services';
  import {PATHS} from '@/const';
  import {CONVERT_LANG_MAP} from '@/map';
  import {mc} from '@/core';
  import Loading from './Loading.svelte';
  import {writeLang} from '@/storage/lang';
  import {genQrcodeUrl, getQrcode, QrType, scanLoginStatus, confirmQrLogin} from './QrLogin';
  import lang from '@/modules/login/LoginPage.lang';
  import {getRedirectTarget, isValidRedirectTarget, clearRedirectTarget} from '@/modules/header/util';

  export let target;

  let qrCodeUrl = `${SERVICES.IMG_URL}/v1/79222719-648a-49c2-92aa-b97242819f61/mask-qr.png`;
  let timeout = 4000;
  let qrType = '';
  let to;
  let qrDomWrap;
  let qrIconDom;
  let isShowQrCode = false;
  let downloadHref;
  let loading = false;

  const LG = getContext('LG');
  const qrRightIcon = `${SERVICES.IMG_URL}/v1/67f5f535-1579-4f89-a919-61512b09dcdf/qr.png`;
  const refreshIcon = `${SERVICES.IMG_URL}/v1/9e9b045a-4ac0-4baa-ad60-eb965c4bc7c8/refresh.png`;
  const successIcon = `${SERVICES.IMG_URL}/v1/356c2021-3b52-4870-a555-80535db17530/complete.png`;
  const qrIconLogo = `${SERVICES.IMG_URL}/v1/e5a70d46-90a4-4ad5-b5de-dc3cc8aeb00e/logo-qr.svg`;
  const loadingImg = `${SERVICES.IMG_URL}/v1/907f8a40-e223-4b9b-a452-31212a5232fd/1.gif`;

  onMount(async () => {
    downloadHref = window.location.origin + PATHS.DOWNLOAD.pathname;
    handleQrClick();
  });

  onDestroy(() => {
    window.removeEventListener('click', eventListener);
  });

  function checkStatus(qrcode) {
    to = setTimeout(async () => {
      const data = await scanLoginStatus({qrcode});
      if (data?.status === QrType.EXPIRED || data?.status === QrType.CONFIRM) {
        qrType = data?.status;
        clearTimeout(to);
        await confirmLogin(data?.status, qrcode);
      } else {
        if (data?.status === QrType.SCAN) {
          qrType = QrType.SCAN;
        }
        clearTimeout(to);
        checkStatus(qrcode);
      }
    }, timeout);
  }

  async function confirmLogin(type, qrcode) {
    if (type === QrType.CONFIRM) {
      const data = await confirmQrLogin({qrcode}, LG);
      if (data) {
        const language = data.lang;
        language && writeLang(CONVERT_LANG_MAP[language] || language);
        mc.send('toast', 'info', {message: LG(lang.LoggedIn)});
        setTimeout(() => {
          window.removeEventListener('click', eventListener);
          const redirectTarget = target || getRedirectTarget();
          if (redirectTarget && isValidRedirectTarget(redirectTarget)) {
            clearRedirectTarget();
            window.location.assign(redirectTarget);
          } else {
            window.location.assign(window.location.origin + PATHS.ACCOUNT_OVERVIEW.pathname);
          }
        }, 1000);
      }
    }
  }

  function handleEmptyClick() {
    window.removeEventListener('click', eventListener, true);
    window.addEventListener('click', eventListener, true);
  }

  function eventListener(e) {
    if (qrIconDom && (qrIconDom === e.target || qrIconDom.contains(e.target))) {
      return;
    }
    if (qrDomWrap && !qrDomWrap.contains(e.target)) {
      if (isShowQrCode) {
        to && clearTimeout(to);
        handleQrHideClick?.();
      }
    }
  }

  async function handleQrClick() {
    to && clearTimeout(to);
    if (isShowQrCode) {
      handleQrHideClick();
      return;
    }
    await loadQrCode();
  }

  async function loadQrCode() {
    isShowQrCode = true;
    handleEmptyClick();
    qrType = null;
    loading = true;
    const data = await getQrcode(LG);
    if (data) {
      const code = JSON.stringify(data);
      genQrcodeUrl(code, (url) => {
        qrCodeUrl = url;
        loading = false;
      });
      checkStatus(data.qrcode);
    }
  }

  function handleQrHideClick() {
    // isShowQrCode = false;
    qrType = '';
    window.removeEventListener('click', eventListener, true);
  }
</script>

<style>
  .qr-wrap {
    align-self: end;
  }
  .qr-layer {
    width: 100%;
  }
  .img-wrap img {
    width: inherit;
    height: inherit;
  }

  .qr-img {
    display: inline-block;
    border-radius: 12px;
    width: 220px;
    height: 220px;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 3px 18px rgba(204, 204, 204, 0.4);
  }

  .qr-fresh {
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 10;
    background-color: rgba(255, 255, 255, 0.9);
  }
  .qr-fresh div {
    height: 100%;
  }
  .fresh-btn {
    padding: 10px 20px;
    background-color: var(--BTN-BG);
    color: var(--BTN-COLOR);
    border-radius: 32px;
  }
  .fresh-btn span {
    width: 20px;
    height: 20px;
  }
  .complete {
    width: 72px;
    height: 72px;
  }
  .logo-qr {
    top: 50%;
    left: 50%;
    z-index: 1;
    display: flex;
    border-radius: 4px;
    padding: 6px;
    width: 20px;
    height: 20px;
    background: #fff;
    transform: translate(-50%, -50%);
  }
  .loading {
    width: 40px;
    height: 40px;
    opacity: 1;
  }
  @media screen and (max-width: 640px) {
    .qr-icon {
      display: none;
    }
  }
</style>

<div class="qr-wrap">
  {#if isShowQrCode}
    <div class="pr" bind:this={qrDomWrap}>
      <div class="qr-layer">
        <div class="qr pr tc pv20 ph20 br12 mt8">
          <div class="df aic jcsb mb20">
            <span class="qr-img img-wrap pr">
              <img src={qrCodeUrl} alt="qr" />
              {#if loading}
                <Loading style="background:#fff;opacity:0.9;">
                  <span slot="rolling" class="loading"><img src={loadingImg} alt="qr" /></span>
                </Loading>
              {/if}
              {#if qrType === QrType.EXPIRED}
                <div class="qr-fresh pa tc">
                  <div class="df fdc aic jcc wp100">
                    <span class="fresh-btn df aic jcc br8 cp f16 lh20 TW" on:click={loadQrCode}><span class="mr8 img-wrap"><img src={refreshIcon} alt="qr" /></span>{LG(lang.QrRefresh)}</span>
                    <span class="mt12 f16 fw2 lh22 T1">{LG(lang.QrExpired)}</span>
                  </div>
                </div>
              {/if}
              {#if qrType === QrType.SCAN || qrType === QrType.CONFIRM}
                <div class="qr-fresh pa tc">
                  <div class="df fdc aic jcc wp100">
                    <span class="complete img-wrap"><img src="https://img.phemex.com/v1/fad75539-70eb-4009-af2a-82437615dcda/frame-2087327667.png" alt="qr" /></span>
                    <span class="mt12 f16 fw2 lh22 T1">{qrType === QrType.SCAN ? LG(lang.QrConfirm) : LG(lang.QrLogin)}</span>
                  </div>
                </div>
              {/if}
            </span>
            <span class="qr-img img-wrap ml12" style="width: 220px;border-radius: 12px 12px 0 0"><img src={qrRightIcon} alt="qr" /></span>
          </div>
          <div class="f14 fw1 lh18 T2">{@html LG(lang.QrTips, {phemexApp: `<a class="T1 fw3 noa" href=${downloadHref}>${LG(lang.PhemexAPP)}</a>`})}</div>
        </div>
      </div>
    </div>
  {/if}
</div>
